import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { CornerBadge } from '@sorare/core/src/components/CornerBadge';
import { Card } from '@sorare/core/src/components/card/Card';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { Captain } from '@sorare/core/src/components/so5/Captain';
import { usePrevious } from '@sorare/core/src/hooks/usePrevious';
import { bonusMessages } from '@sorare/core/src/lib/bonus';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';
import { LevelUpAnimation } from '@sorare/marketplace/src/components/card/levelup/LevelUpAnimation';

import type { CardBlock_appearance } from './__generated__/index.graphql.ts';

const Root = styled.div`
  width: calc(var(--card-aspect-ratio) * 240px);
  margin: auto;
  position: relative;
`;
const CornerBadges = styled(Vertical).attrs({ gap: 1 })`
  position: absolute;
  top: var(--unit);
  right: 0;
  align-items: end;
`;
const CornerBadgeWrapper = styled(Horizontal)`
  position: relative;
`;
const IconWrapper = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  left: calc(100% + var(--intermediate-unit));
  width: var(--double-unit);
`;

const getCaptainBonus = (appearance: CardBlock_appearance) => {
  const { powerBreakdownOrPowerPointsBreakdown } = appearance;

  if (!powerBreakdownOrPowerPointsBreakdown) {
    return null;
  }

  return isType(powerBreakdownOrPowerPointsBreakdown, 'PowerBreakdown')
    ? powerBreakdownOrPowerPointsBreakdown.captainBasisPoints
    : powerBreakdownOrPowerPointsBreakdown.captainPoints;
};

const getScarcityBonus = (appearance: CardBlock_appearance) => {
  const { powerBreakdownOrPowerPointsBreakdown } = appearance;

  if (!powerBreakdownOrPowerPointsBreakdown) {
    return null;
  }

  return isType(powerBreakdownOrPowerPointsBreakdown, 'PowerBreakdown')
    ? powerBreakdownOrPowerPointsBreakdown.scarcityBasisPoints
    : powerBreakdownOrPowerPointsBreakdown.scarcityPoints;
};

type Props = {
  appearance: CardBlock_appearance;
};

export const CardBlock = ({ appearance }: Props) => {
  const [levelUpAnimating, setLevelUpAnimating] = useState(false);

  const { powerBreakdownOrPowerPointsBreakdown, anyCard } = appearance;

  const previousGrade = usePrevious(anyCard?.grade);

  const captainBonus = getCaptainBonus(appearance);
  const scarcityBonus = getScarcityBonus(appearance);

  if (!appearance.pictureUrl) {
    return null;
  }

  if (
    !levelUpAnimating &&
    anyCard &&
    typeof previousGrade === 'number' &&
    previousGrade !== anyCard.grade
  ) {
    setLevelUpAnimating(true);
  }

  return (
    <Root>
      <LevelUpAnimation
        animate={levelUpAnimating}
        onAnimationEnd={() => setLevelUpAnimating(false)}
      >
        {appearance.anyCard ? (
          <Card card={appearance.anyCard} width={320} />
        ) : (
          <CardImg
            src={appearance.pictureUrl ?? undefined}
            // Always get 320px placeholder
            // This image is displayed between 100 and 145 px wide
            width={320}
            dpis={1}
            alt=""
          />
        )}
      </LevelUpAnimation>
      {!levelUpAnimating && (
        <CornerBadges>
          {!!captainBonus && (
            <Tooltip
              placement="left"
              title={
                <LabelS bold>
                  <FormattedMessage {...bonusMessages.captain} />
                </LabelS>
              }
            >
              <CornerBadgeWrapper>
                <CornerBadge color="var(--c-rewards-pink)">
                  <LabelS bold color="var(--c-white)">
                    <FormattedNumber
                      value={
                        isType(
                          powerBreakdownOrPowerPointsBreakdown,
                          'PowerBreakdown'
                        )
                          ? captainBonus / 10000
                          : captainBonus
                      }
                      style={
                        isType(
                          powerBreakdownOrPowerPointsBreakdown,
                          'PowerBreakdown'
                        )
                          ? 'percent'
                          : undefined
                      }
                      signDisplay="always"
                    />
                  </LabelS>
                </CornerBadge>
                <IconWrapper>
                  <Captain active size={2} />
                </IconWrapper>
              </CornerBadgeWrapper>
            </Tooltip>
          )}
          {!!scarcityBonus && (
            <Tooltip
              placement="left"
              title={
                <LabelS bold>
                  <FormattedMessage {...bonusMessages.scarcity} />
                </LabelS>
              }
            >
              <CornerBadgeWrapper>
                <CornerBadge color={getRarityColor(appearance.rarity)}>
                  <LabelS bold color="var(--c-white)">
                    <FormattedNumber
                      value={
                        isType(
                          powerBreakdownOrPowerPointsBreakdown,
                          'PowerBreakdown'
                        )
                          ? scarcityBonus / 10000
                          : scarcityBonus
                      }
                      style={
                        isType(
                          powerBreakdownOrPowerPointsBreakdown,
                          'PowerBreakdown'
                        )
                          ? 'percent'
                          : undefined
                      }
                      signDisplay="always"
                    />
                  </LabelS>
                </CornerBadge>
                <IconWrapper>
                  <ScarcityIcon rarity={appearance.rarity} size={3} />
                </IconWrapper>
              </CornerBadgeWrapper>
            </Tooltip>
          )}
        </CornerBadges>
      )}
    </Root>
  );
};

CardBlock.fragments = {
  appearance: gql`
    fragment CardBlock_appearance on AppearanceInterface {
      id
      rarity
      pictureUrl
      powerBreakdownOrPowerPointsBreakdown {
        ... on PowerBreakdown {
          captainBasisPoints
          scarcityBasisPoints
        }
        ... on PowerPointsBreakdown {
          captainPoints
          scarcityPoints
        }
      }
      anyCard {
        slug
        grade
        pictureUrl
        ...Card_anyCard
      }
    }
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<CardBlock_appearance>,
};
